import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import ListLoadError from '../../components/admin/ListLoadError';
import api from '../../services/api';
import { approveRefill, denyRefill } from '../../services/orderService';
import ApiTemplatesModal from './ApiTemplatesModal';
import { calculateOrderCostCents } from './orderCostMath';
import {
  FILTER_STATUSES,
  computeStatusOptions,
  formatStatusLabel,
  isStatusLocked
} from './orderStatusOptions';
import styles from './OrdersPage.module.css';

/*
 * ⛔ THE STATUS LISTS LIVE IN `./orderStatusOptions.js` AND THERE ARE TWO OF THEM.
 * A single `STATUSES` array used to feed both the filter buttons below and the editable
 * row-menu select, which offered `on_hold` (not an order status — a list filter resolved
 * server-side) and `refill` (no transition key), so both 400'd from every state. The filter
 * vocabulary keeps all 10 entries; the select derives its options from the server's real
 * decision table. Card 7232, 2026-09-02.
 */

// The gate MUST use the shared four-column helper: a three-column sum hid the profit of any
// order whose only cost is shipping, while the value below comes from the backend's four. (card 28342)
const formatProfit = (order) => {
  const costs = calculateOrderCostCents(order);
  if (costs === 0) return { display: '—', color: 'inherit' };
  const profit = order.profit ?? 0;
  const dollars = (profit / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    display: `$${dollars}`,
    color: profit >= 0 ? '#15803d' : '#b91c1c'
  };
};

// One compact line: lead product name, then a "+N" affordance rather than a stacked list.
const formatProductSummary = (order) => {
  const items = order.items || [];
  if (items.length === 0) return { name: '—', tier: '', extra: 0 };
  const [first] = items;
  const name = first.productName || first.product?.name || 'Unknown Product';
  const qty = first.quantity > 1 ? ` x${first.quantity}` : '';
  return {
    name: `${name}${qty}`,
    tier: first.pricingTierName || first.pricingTier?.name || '',
    extra: items.length - 1
  };
};

/*
 * REQ-54 — the period a money column covers. Derived from the rows actually RENDERED,
 * never from a filter value, so the label cannot claim a span the table is not showing.
 * This list has no date filter (`listOrders` accepts only page/limit/sort/status/search/
 * userId/provider), so the applied status, provider, search and page ARE the period
 * control: narrowing any of them re-renders a different row set and this label follows it.
 */
const derivePeriodLabel = (rows) => {
  const stamps = rows.map(({ createdAt }) => new Date(createdAt).getTime()).filter(Number.isFinite);
  if (stamps.length === 0) return 'no rows';

  const from = new Date(Math.min(...stamps));
  const to = new Date(Math.max(...stamps));
  const dayMonth = { month: 'short', day: 'numeric' };
  const dayMonthYear = { ...dayMonth, year: 'numeric' };

  if (from.toDateString() === to.toDateString()) return to.toLocaleDateString('en-US', dayMonthYear);
  return `${from.toLocaleDateString('en-US', from.getFullYear() === to.getFullYear() ? dayMonth : dayMonthYear)} – ${to.toLocaleDateString('en-US', dayMonthYear)}`;
};

// A money column's heading: the name, then the period and source beneath it (REQ-54).
const MoneyColumnHeading = ({ name, meta, help }) => (
  <th>
    <span className={styles.colTitle}>{name}</span>
    <span className={styles.colMeta} title={help}>{meta}</span>
  </th>
);

/*
 * Compact accessible overflow — the rehomed Actions column (REQ-35/REQ-39).
 * Every control below keeps the EXACT permission expression it had in the old
 * Actions cell, so a role without `orders.edit` is refused here as it was there.
 */
const RowActionsMenu = ({ order, canEdit, onResolveHold, onRefill, onUpdateStatus }) => {
  const [open, setOpen] = useState(false);
  const showHold = canEdit && order.holdStatus === 'held';
  const showRefill = canEdit && order.status === 'refill';

  const close = () => setOpen(false);
  const run = (fn) => (e) => { e.stopPropagation(); close(); fn(); };

  return (
    <div
      className={styles.rowActions}
      data-row-interactive="true"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) close(); }}
    >
      <button
        type="button"
        className={styles.rowActionsTrigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for order ${order.orderNumber}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        &#8943;
      </button>

      {open && (
        <div className={styles.rowActionsMenu} role="menu" aria-label={`Actions for order ${order.orderNumber}`}>
          {showHold && (
            <>
              <button type="button" role="menuitem" className={styles.menuApprove} onClick={run(() => onResolveHold(order.id, 'approved'))}>
                Approve
              </button>
              <button type="button" role="menuitem" className={styles.menuDeny} onClick={run(() => onResolveHold(order.id, 'denied'))}>
                Deny
              </button>
            </>
          )}
          {showRefill && (
            <>
              <button type="button" role="menuitem" className={styles.menuApprove} onClick={run(() => onRefill(order, 'approve'))}>
                Approve Refill
              </button>
              <button type="button" role="menuitem" className={styles.menuDeny} onClick={run(() => onRefill(order, 'deny'))}>
                Deny Refill
              </button>
            </>
          )}
          <label className={styles.menuStatusLabel} htmlFor={`status-${order.id}`}>Status</label>
          <select
            id={`status-${order.id}`}
            className={styles.statusSelect}
            value={order.status}
            disabled={!canEdit || isStatusLocked(order.status)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); onUpdateStatus(order.id, e.target.value); close(); }}
          >
            {computeStatusOptions(order.status).map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const AdminOrdersPage = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('orders.edit');
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showApiTemplates, setShowApiTemplates] = useState(false);
  const [refillModal, setRefillModal] = useState(null); // { order, action: 'approve' | 'deny' }
  const [refillSubmitting, setRefillSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef(null);
  const searchRef = useRef('');

  useEffect(() => {
    loadOrders();
  }, [selectedStatus, selectedProvider]);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadOrders(1);
    }, 400);
  }, [selectedStatus, selectedProvider]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    loadOrders(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    searchRef.current = '';
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    loadOrders(1);
  };

  searchRef.current = searchQuery;

  const loadOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedProvider) params.append('provider', selectedProvider);
      if (searchRef.current && searchRef.current.trim()) params.append('search', searchRef.current.trim());

      const response = await api.get(`/admin/orders?${params}`);
      setOrders(response.orders || []);
      setPagination(response.pagination);
      setLoadError(false);
    } catch (error) {
      // A failed fetch must NOT fall through to `orders.length === 0`, which renders
      // "No orders found" — indistinguishable from a genuinely empty order book once
      // the toast auto-dismisses. Record the failure so the list can say so instead.
      setLoadError(true);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const openOrder = (orderId) => navigate(`/admin/orders/${orderId}`);

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated');
      loadOrders();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleResolveHold = async (orderId, decision) => {
    const action = decision === 'approved' ? 'approve' : 'deny';
    const consequence = decision === 'approved'
      ? 'capture the order payment and release the consultation fee hold'
      : 'charge the consultation fee and release the order payment hold';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this order? This will ${consequence}.`
    );
    if (!confirmed) return;

    try {
      await api.patch(`/admin/orders/${orderId}/resolve`, { decision });
      toast.success(`Order ${action === 'approve' ? 'approved' : 'denied'} successfully`);
      loadOrders();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} order`);
    }
  };

  const handleConfirmRefill = async () => {
    if (!refillModal || refillSubmitting) return;
    const { order, action } = refillModal;
    setRefillSubmitting(true);
    try {
      await (action === 'approve' ? approveRefill(order.id) : denyRefill(order.id));
      toast.success(`Refill ${action === 'approve' ? 'approved' : 'denied'} — ${order.orderNumber}`);
      setRefillModal(null);
      loadOrders(pagination?.page || 1);
    } catch (error) {
      toast.error(error.message || `Failed to ${action} refill`);
    } finally {
      setRefillSubmitting(false);
    }
  };

  // Pure reads of the rendered rows — recomputed on every render so neither can
  // outlive the row set it describes.
  const periodLabel = derivePeriodLabel(orders);
  const coverage = `Covers the ${orders.length} order${orders.length === 1 ? '' : 's'} listed below: ${periodLabel}.`;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Orders</h1>
          <p>Manage customer orders</p>
        </div>
        <button
          onClick={() => loadOrders(pagination?.page || 1)}
          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 500, marginRight: '8px' }}
        >
          ↻ Refresh
        </button>
        <button className={styles.apiTemplatesBtn} onClick={() => setShowApiTemplates(true)}>
          API Templates
        </button>
      </div>

      {showApiTemplates && <ApiTemplatesModal onClose={() => setShowApiTemplates(false)} />}


      {/* Search Bar */}
      <form className={styles.searchBar} onSubmit={handleSearchSubmit}>
        <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by order number, customer name, email, or product..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchQuery && (
          <button type="button" className={styles.searchClear} onClick={handleClearSearch}>
            &times;
          </button>
        )}
      </form>
      {/* Provider Toggle + Status Filter */}
      <div className={styles.filters}>
        <div className={styles.providerToggle}>
          {[['', 'All'], ['doctornow', 'Dr. Now'], ['drcare', 'DrCare']].map(([value, label]) => (
            <button
              key={label}
              className={`${styles.providerBtn} ${selectedProvider === value ? styles.providerActive : ''}`}
              onClick={() => setSelectedProvider(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className={`${styles.filterBtn} ${selectedStatus === '' ? styles.active : ''}`}
          onClick={() => setSelectedStatus('')}
        >
          All
        </button>
        {FILTER_STATUSES.map((status) => (
          <button
            key={status}
            className={`${styles.filterBtn} ${selectedStatus === status ? styles.active : ''}`}
            onClick={() => setSelectedStatus(status)}
          >
            {formatStatusLabel(status)}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : loadError ? (
          <ListLoadError message="Could not load orders" onRetry={() => loadOrders(1)} />
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <MoneyColumnHeading
                    name="Total"
                    meta={`${periodLabel} · order total`}
                    help={`Amount charged for each order, from the order's own total. ${coverage}`}
                  />
                  <MoneyColumnHeading
                    name="Profit"
                    meta={`${periodLabel} · modeled`}
                    help={`Modeled, not booked: order total minus consultation, medication and misc cost. Blank when no cost is recorded. ${coverage}`}
                  />
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const product = formatProductSummary(order);
                  return (
                    <tr
                      key={order.id}
                      onClick={(e) => {
                        if (e.target.closest('[data-row-interactive]')) return;
                        if (['SELECT', 'OPTION', 'BUTTON', 'A'].includes(e.target.tagName)) return;
                        openOrder(order.id);
                      }}
                      style={{ cursor: 'pointer' }}
                      className={styles.clickableRow}
                    >
                      <td data-label="Order">
                        <div className={styles.orderCell}>
                          <a
                            className={styles.orderNumber}
                            href={`/admin/orders/${order.id}`}
                            data-row-interactive="true"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openOrder(order.id); }}
                          >
                            {order.orderNumber}
                          </a>
                          <span className={styles.itemCount}>
                            {order.itemCount || order.items?.length || 0} item{(order.itemCount || order.items?.length || 0) !== 1 ? 's' : ''}
                          </span>
                          {selectedProvider === '' && (
                            <span className={`${styles.providerBadge} ${order.lifecycleStages ? styles.providerDn : styles.providerDrcare}`}>
                              {order.lifecycleStages ? 'Dr. Now' : 'DrCare'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Customer">
                        {order.user?.id || order.userId ? (
                          <a
                            className={styles.customerName}
                            href={'/admin/members/' + (order.user?.id || order.userId)}
                            data-row-interactive="true"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/admin/members/' + (order.user?.id || order.userId), { state: { from: 'orders' } }); }}
                            style={{ color: '#e8732a', textDecoration: 'none', cursor: 'pointer' }}
                          >
                            {order.user?.fullName || order.userName || order.user?.email || 'Unknown'}
                          </a>
                        ) : (
                          <span className={styles.customerName}>{order.user?.fullName || order.userName || order.user?.email || 'Unknown'}</span>
                        )}
                      </td>
                      <td data-label="Product">
                        <div className={styles.productCell}>
                          <span className={styles.productName}>{product.name}</span>
                          {product.tier && <span className={styles.productTier}>{product.tier}</span>}
                          {product.extra > 0 && <span className={styles.productMore}>+{product.extra}</span>}
                        </div>
                      </td>
                      <td className={styles.total} data-label="Total">{order.pricing?.formattedTotal || order.total}</td>
                      <td className={styles.profit} style={{ color: formatProfit(order).color }} data-label="Profit">
                        {formatProfit(order).display}
                      </td>
                      <td className={styles.date} data-label="Date">
                        <div className={styles.dateCell}>
                          <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                          <RowActionsMenu
                            order={order}
                            canEdit={canEdit}
                            onResolveHold={handleResolveHold}
                            onRefill={(o, action) => setRefillModal({ order: o, action })}
                            onUpdateStatus={handleUpdateStatus}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && orders.length === 0 && (
          <div className={styles.empty}>No orders found</div>
        )}
      </Card>

      {/* Refill Approve/Deny Confirmation Modal */}
      {refillModal && (
        <Modal
          isOpen
          onClose={() => { if (!refillSubmitting) setRefillModal(null); }}
          title={refillModal.action === 'approve' ? 'Approve Refill' : 'Deny Refill'}
          size="small"
        >
          <p className={styles.refillModalText}>
            {refillModal.action === 'approve'
              ? `Approve the refill for order ${refillModal.order.orderNumber}? The order status will change to Approved, the customer will receive a confirmation email, the support ticket will be resolved, and fulfillment will begin.`
              : `Deny the refill for order ${refillModal.order.orderNumber}? The order will be marked Denied and the support ticket closed.`}
          </p>
          {refillModal.action === 'deny' && (
            <p className={styles.refillModalWarning}>
              Warning: this will issue a refund (or void) of the refill charge back to the customer&apos;s card. This cannot be undone.
            </p>
          )}
          <div className={styles.refillModalFooter}>
            <button
              className={styles.cancelBtn}
              onClick={() => setRefillModal(null)}
              disabled={refillSubmitting}
            >
              Cancel
            </button>
            <button
              className={refillModal.action === 'approve' ? styles.approveBtn : styles.denyBtn}
              onClick={handleConfirmRefill}
              disabled={refillSubmitting}
            >
              {refillSubmitting
                ? <LoadingSpinner size="small" />
                : refillModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Deny'}
            </button>
          </div>
        </Modal>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={!pagination.hasPrevPage}
            onClick={() => loadOrders(pagination.page - 1)}
          >
            Previous
          </Button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <Button
            variant="outline"
            size="small"
            disabled={!pagination.hasNextPage}
            onClick={() => loadOrders(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
